import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function InviteAcceptPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav(`/login?next=/invite/${token}`, { replace: true }); return; }
    api.get(`/invitations/lookup?token=${token}`)
      .then((r) => setPreview(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invitation not found or expired"));
  }, [user, authLoading, token, nav]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api.post("/invitations/accept", { token });
      localStorage.setItem("aevum_active_org_v1", r.data.org_id);
      toast.success(r.data.already_member ? "You're already in this workspace" : `Joined ${preview.org.name}`);
      // Full reload so all providers refetch scoped by the new active org.
      window.location.href = "/";
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (authLoading || (!preview && !err)) return <div className="min-h-screen flex items-center justify-center text-sm text-surface-onVariant">Loading invitation…</div>;

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 relative overflow-hidden bg-surface">
      <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-primary-container/60 blur-[120px]" />
      <div className="absolute -bottom-32 -right-24 w-[420px] h-[420px] rounded-full bg-tertiary-container/60 blur-[120px]" />
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-surface-container rounded-hero p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto"><Sparkles className="w-6 h-6" /></div>
          {err ? (
            <>
              <h1 className="text-2xl font-medium text-surface-on mt-6">Invitation unavailable</h1>
              <p className="text-sm text-surface-onVariant mt-2">{err}</p>
              <button onClick={() => nav("/")} className="mt-6 rounded-full bg-primary text-primary-foreground font-medium px-5 py-2.5">Back to Aevum</button>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-medium text-surface-on mt-6 tracking-tight">You're invited</h1>
              <p className="text-sm text-surface-onVariant mt-2">Join <span className="font-medium text-surface-on">{preview.org.name}</span> as <span className="font-medium text-surface-on">{preview.invitation.role}</span>.</p>
              <button data-testid="invite-accept-btn" onClick={accept} disabled={busy}
                className="mt-8 w-full rounded-full bg-primary text-primary-foreground font-medium py-3 active:scale-[0.98] hover:brightness-110 disabled:opacity-60 transition-[transform,filter] duration-200 ease-md">
                {busy ? "Joining…" : "Accept & join"}
              </button>
              <button onClick={() => nav("/")} className="mt-2 w-full text-sm text-surface-onVariant hover:text-surface-on">Not now</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
